/**
 * The wiki's categories (issue 1568): the DM's tree (People › Clergy), any
 * depth, a page in as many as the DM likes. The server sends each reader
 * their own tree (`categories`, each with its parent and the count of its
 * branch's pages they may read) and each page's `category_ids`; this only
 * arranges them. A page's `type` is its kind, not a category.
 *
 * Pages in no category are Uncategorised, which only the DM's tree shows.
 */
export const UNCATEGORISED = 'Uncategorised';

const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
const byTitle = (a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });

/**
 * [{ id, slug, name, count, pages, children }] — the roots, alphabetical,
 * each with the pages filed directly in it and its sub-categories. A
 * category whose parent the reader is not sent is a root (it cannot be, as
 * the server counts a branch, but the tree holds up).
 */
export function categoryTree(categories, pages) {
  const nodes = new Map((categories || []).map((c) => [c.id, { ...c, pages: [], children: [] }]));
  for (const p of pages || []) for (const id of p.category_ids || []) if (nodes.has(id)) nodes.get(id).pages.push(p);
  const roots = [];
  for (const node of nodes.values()) {
    const parent = node.parent_id && nodes.get(node.parent_id);
    (parent && parent !== node ? parent.children : roots).push(node);
  }
  const sort = (list) => {
    list.sort(byName);
    for (const n of list) { n.pages.sort(byTitle); sort(n.children); }
    return list;
  };
  return sort(roots);
}

/** The pages in no category, alphabetical. */
export function uncategorised(pages) {
  return (pages || []).filter((p) => !(p.category_ids || []).length).sort(byTitle);
}

/** A category and its ancestors, the top first: [{ id, slug, name }]. Empty for an unknown id. */
export function categoryPath(categories, id) {
  const byId = new Map((categories || []).map((c) => [c.id, c]));
  const path = [];
  const seen = new Set();
  for (let at = byId.get(id); at && !seen.has(at.id); at = byId.get(at.parent_id)) {
    seen.add(at.id);
    path.unshift(at);
  }
  return path;
}

/** A category's name with its ancestors', "People › Clergy", for a choice in a list. */
export function categoryLabel(categories, id) {
  return categoryPath(categories, id).map((c) => c.name).join(' › ');
}

/** A crest's letters until the campaign has an image: the initials of its first two words that are not "the", "a" or "of". */
export function crestInitials(name) {
  const words = String(name || '').split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
  const kept = words.filter((w) => !/^(the|a|an|of)$/i.test(w));
  return (kept.length ? kept : words).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}
